<script setup lang="ts">
/**
 * 点餐页：左边分类、右边菜品、底部购物车。
 *
 * 触屏上的几个取舍：
 *   · 分类用左侧竖排大按钮，而不是顶部横向滚动 —— 横滚在触屏上很难拖准；
 *   · 菜品点击直接加购（有规格的弹规格层），不需要先进详情页；
 *   · 售罄的菜灰掉但仍显示，顾客找得到、看得见「已售罄」；
 *   · 购物车栏常驻底部，金额随时可见。
 */
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { MenuProduct } from '../api/types';
import ProductCard from '../components/ProductCard.vue';
import VariantDialog from '../components/VariantDialog.vue';
import CartBar from '../components/CartBar.vue';
import { kiosk } from '../stores/kiosk';

const router = useRouter();
const activeCategoryId = ref<string | null>(null);
const picking = ref<MenuProduct | null>(null);
const showCart = ref(false);

const categories = computed(() => kiosk.state.menu);
const activeCategory = computed(
  () => categories.value.find((c) => c.id === activeCategoryId.value) ?? categories.value[0] ?? null,
);

/** 每道菜在购物车里的份数（卡片角标） */
const quantityByProduct = computed(() => {
  const map = new Map<string, number>();
  for (const row of kiosk.state.cart) {
    map.set(row.productId, (map.get(row.productId) ?? 0) + row.quantity);
  }
  return map;
});

/** 扫桌码模式（顾客用自己手机）：不显示「返回首页」，改为显示桌号 */
const isTableMode = computed(() => kiosk.state.config.mode === 'table');

onMounted(() => {
  if (categories.value.length === 0) void kiosk.loadMenu();
  if (!activeCategoryId.value) activeCategoryId.value = categories.value[0]?.id ?? null;
});

/** 点了一道菜：有规格就弹规格，没有就直接加购 */
function pick(product: MenuProduct): void {
  if (product.variants.length > 0) {
    picking.value = product;
    return;
  }
  kiosk.addToCart({
    productId: product.id,
    productName: product.name,
    unitPriceCents: product.priceCents,
    imageUrl: product.imageUrl,
  });
}

function confirmVariant(payload: {
  variantIds: string[];
  variantText: string | null;
  unitPriceCents: number;
  quantity: number;
  remark: string | null;
}): void {
  if (!picking.value) return;
  kiosk.addToCart({
    productId: picking.value.id,
    productName: picking.value.name,
    variantIds: payload.variantIds,
    variantText: payload.variantText,
    unitPriceCents: payload.unitPriceCents,
    quantity: payload.quantity,
    remark: payload.remark,
    imageUrl: picking.value.imageUrl,
  });
  picking.value = null;
}

async function checkout(): Promise<void> {
  const ok = await kiosk.previewOrder();
  if (ok) void router.push('/confirm');
}
</script>

<template>
  <div class="screen menu">
    <div class="topbar">
      <div>
        <div class="topbar__title">
          点餐
          <!-- 扫桌码进来的必须让顾客一眼确认「是哪一桌」，上错桌比点错菜更麻烦 -->
          <span v-if="isTableMode && kiosk.state.table" class="table-badge" data-testid="table-badge">
            {{ kiosk.state.table.code }} 桌
          </span>
        </div>
        <div class="topbar__sub">
          <template v-if="isTableMode">
            {{ kiosk.state.config.tableToken ? '' : '' }}点菜后请在下方「去结算」，出餐会叫取餐号
          </template>
          <template v-else>{{ kiosk.state.config.storeCode }} · 点菜后请到屏幕下方「去结算」</template>
        </div>
      </div>
      <button
        v-if="!isTableMode"
        class="btn btn--ghost"
        data-testid="back-home"
        @click="router.push('/')"
      >
        返回首页
      </button>
    </div>

    <div class="menu__body screen__body">
      <!-- 分类 -->
      <aside class="cats">
        <button
          v-for="category in categories"
          :key="category.id"
          class="cats__item"
          :class="{ 'cats__item--on': category.id === activeCategory?.id }"
          @click="activeCategoryId = category.id"
        >
          {{ category.name }}
        </button>
      </aside>

      <!-- 菜品 -->
      <section class="gridwrap">
        <div v-if="kiosk.state.menuLoading" class="loading">正在加载菜单…</div>

        <div v-else-if="kiosk.state.menuError" class="empty">
          <div class="empty__icon">⚠️</div>
          <div>{{ kiosk.state.menuError }}</div>
          <button class="btn btn--primary" @click="kiosk.loadMenu()">重新加载</button>
        </div>

        <div v-else-if="!activeCategory || activeCategory.products.length === 0" class="empty">
          <div class="empty__icon">🍽️</div>
          <div>这个分类暂时没有可点的菜</div>
        </div>

        <div v-else class="grid" data-testid="product-grid">
          <ProductCard
            v-for="product in activeCategory.products"
            :key="product.id"
            :product="product"
            :quantity="quantityByProduct.get(product.id) ?? 0"
            @pick="pick"
          />
        </div>
      </section>
    </div>

    <CartBar
      data-testid="cart-bar"
      @checkout="checkout"
      @clear="kiosk.state.cart = []"
      @open="showCart = true"
    />

    <VariantDialog :product="picking" @close="picking = null" @confirm="confirmVariant" />

    <!-- 购物车明细（不跳页，直接在这里改数量） -->
    <div v-if="showCart" class="overlay" @click.self="showCart = false">
      <div class="dialog">
        <div class="dialog__title">已经点的菜</div>
        <div class="dialog__body cartlist">
          <div v-for="row in kiosk.state.cart" :key="row.key" class="cartlist__row">
            <div class="cartlist__name">
              {{ row.productName }}
              <span v-if="row.variantText" class="muted">· {{ row.variantText }}</span>
            </div>
            <div class="cartlist__ops">
              <button class="qty" @click="kiosk.changeQuantity(row.key, -1)">−</button>
              <span class="qty__value">{{ row.quantity }}</span>
              <button class="qty" @click="kiosk.changeQuantity(row.key, 1)">＋</button>
              <button class="btn btn--ghost cartlist__del" @click="kiosk.removeItem(row.key)">删除</button>
            </div>
          </div>
          <div v-if="kiosk.state.cart.length === 0" class="muted center">购物车是空的</div>
        </div>
        <div class="dialog__footer">
          <button class="btn btn--primary" @click="showCart = false">继续点餐</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table-badge {
  display: inline-block;
  margin-left: 10px;
  padding: 2px 12px;
  border-radius: 999px;
  background: var(--brand, #8b1f1f);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  vertical-align: middle;
}

.menu__body {
  display: flex;
  gap: 16px;
}

.cats {
  width: 200px;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: auto;
}

.cats__item {
  min-height: 78px;
  border-radius: 14px;
  background: var(--panel);
  border: 2px solid var(--line);
  font-size: 20px;
  font-weight: 700;
}

.cats__item--on {
  background: rgba(217, 43, 43, 0.16);
  border-color: var(--brand);
  color: #ffd7d7;
}

.gridwrap {
  flex: 1;
  min-width: 0;
  overflow: auto;
  padding-right: 4px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
  padding-bottom: 10px;
}

.empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--muted);
  font-size: 20px;
}

.empty__icon {
  font-size: 52px;
}

.cartlist {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cartlist__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  background: var(--panel-2);
  border-radius: 14px;
}

.cartlist__name {
  flex: 1;
  font-size: 20px;
}

.cartlist__ops {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qty {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: var(--panel);
  border: 2px solid var(--line);
  font-size: 26px;
  font-weight: 700;
}

.qty__value {
  min-width: 40px;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
}

.cartlist__del {
  min-height: 60px;
  font-size: 16px;
  padding: 0 16px;
}

@media (max-width: 860px) {
  /* 手机竖屏：分类改成顶部横向滑动条，把整个宽度让给菜品 */
  .menu__body {
    flex-direction: column;
    gap: 10px;
  }

  .cats {
    width: 100%;
    flex: 0 0 auto;
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 8px;
    padding-bottom: 4px;
    /* 手指滑动跟手，且不要出现滚动条 */
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .cats::-webkit-scrollbar {
    display: none;
  }

  .cats__item {
    flex: 0 0 auto;
    min-height: 52px;
    padding: 0 18px;
    font-size: 16px;
    white-space: nowrap;
    border-radius: 12px;
  }

  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .cartlist__row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .cartlist__ops {
    justify-content: flex-end;
  }

  .qty {
    width: 48px;
    height: 48px;
    font-size: 22px;
  }
}

</style>
